import React, { useState, useRef } from 'react';
import { SocialMediaPlannerService } from '../../services/socialMediaPlannerService';
import { MediaItem } from '../../types/socialMediaPlanner';

interface MediaUploaderProps {
  onMediaUpload?: (media: MediaItem) => void;
  maxSizeInBytes?: number;
  acceptedFileTypes?: string[];
}

/**
 * Media Uploader component for uploading images and videos using Tailwind CSS
 */
const MediaUploaderTailwind: React.FC<MediaUploaderProps> = ({
  onMediaUpload,
  maxSizeInBytes = 10 * 1024 * 1024, // 10MB default
  acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4']
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [altText, setAltText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState<boolean>(false);
  
  // Handle drop zone click
  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    
    // Validate file size
    if (selectedFile.size > maxSizeInBytes) {
      setError(`File size exceeds the maximum allowed size (${maxSizeInBytes / (1024 * 1024)}MB)`);
      return;
    }
    
    // Validate file type
    if (!acceptedFileTypes.includes(selectedFile.type)) {
      setError(`File type not supported. Accepted types: ${acceptedFileTypes.join(', ')}`);
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(selectedFile);
    setPreview(previewUrl);
    
    // Open upload dialog
    setUploadDialogOpen(true);
  };
  
  // Handle drag over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  // Handle drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;
    
    // Validate file size
    if (droppedFile.size > maxSizeInBytes) {
      setError(`File size exceeds the maximum allowed size (${maxSizeInBytes / (1024 * 1024)}MB)`);
      return;
    }
    
    // Validate file type
    if (!acceptedFileTypes.includes(droppedFile.type)) {
      setError(`File type not supported. Accepted types: ${acceptedFileTypes.join(', ')}`);
      return;
    }
    
    setFile(droppedFile);
    setError(null);
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(droppedFile);
    setPreview(previewUrl);
    
    // Open upload dialog
    setUploadDialogOpen(true);
  };
  
  // Handle upload
  const handleUpload = async () => {
    if (!file) return;
    
    setLoading(true);
    setProgress(0);
    
    try {
      // Create an interval to simulate upload progress
      // In a real implementation, this would be replaced with actual upload progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload the file
      const plannerService = new SocialMediaPlannerService();
      const uploadedMedia = await plannerService.uploadMedia(file, altText);
      
      // Clear the interval and set progress to 100%
      clearInterval(progressInterval);
      setProgress(100);
      
      // Call the callback with the uploaded media
      if (onMediaUpload) {
        onMediaUpload(uploadedMedia);
      }
      
      // Reset the state
      setTimeout(() => {
        setFile(null);
        setPreview(null);
        setAltText('');
        setLoading(false);
        setProgress(0);
        setUploadDialogOpen(false);
      }, 1000);
      
    } catch (err: any) {
      console.error('Error uploading media:', err);
      setError(err.message || 'Failed to upload media');
      setLoading(false);
    }
  };
  
  // Handle cancel
  const handleCancel = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setFile(null);
    setPreview(null);
    setAltText('');
    setError(null);
    setUploadDialogOpen(false);
  };
  
  // Render media preview
  const renderMediaPreview = () => {
    if (!preview) return null;
    
    const isVideo = file?.type.startsWith('video/');
    
    return (
      <div className="w-full h-52 mt-4 relative overflow-hidden rounded-md">
        {isVideo ? (
          <video src={preview} controls className="w-full h-full object-cover" />
        ) : (
          <img src={preview} alt="Preview" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
          <button 
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md shadow-sm"
            onClick={() => setUploadDialogOpen(true)}
          >
            Edit Details
          </button>
        </div>
        <div className="absolute top-2 right-2 z-10">
          <button
            className="bg-white bg-opacity-70 p-1 rounded-full hover:bg-opacity-100 text-red-600"
            onClick={handleCancel}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes.join(',')}
        onChange={handleFileChange}
        className="hidden"
      />
      
      {!file ? (
        <div
          onClick={handleDropZoneClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className="p-6 flex flex-col items-center cursor-pointer border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-blue-500 hover:bg-gray-100 transition-all duration-300"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <h3 className="mt-4 text-lg font-medium">Drag & Drop Media Here</h3>
          <p className="mt-1 text-sm text-gray-500">or click to browse</p>
          <div className="mt-4 flex gap-2">
            <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-gray-300 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              Images
            </span>
            <span className="inline-flex items-center px-3 py-1 text-xs rounded-full border border-gray-300 text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
              </svg>
              Videos
            </span>
          </div>
          <p className="mt-4 text-xs text-gray-500">Max size: {maxSizeInBytes / (1024 * 1024)}MB</p>
        </div>
      ) : (
        renderMediaPreview()
      )}
      
      {error && (
        <p className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
      
      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Upload Media</h3>
            </div>
            
            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="h-52 flex items-center justify-center border border-gray-200 rounded-md overflow-hidden">
                    {file?.type.startsWith('video/') ? (
                      <video src={preview || ''} height="200" controls className="max-h-full max-w-full" />
                    ) : (
                      <img 
                        src={preview || ''} 
                        alt="Preview" 
                        className="max-h-full max-w-full" 
                      />
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    {file?.name} {file?.size !== undefined && `(${(file.size / 1024).toFixed(2)} KB)`}
                  </p>
                </div>
                <div>
                  <div className="mt-4 md:mt-0">
                    <label htmlFor="alt-text" className="block text-sm font-medium text-gray-700">Alt Text</label>
                    <input
                      id="alt-text"
                      type="text"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      value={altText}
                      onChange={(e) => setAltText(e.target.value)}
                      placeholder="Describe this media for accessibility"
                    />
                    <p className="mt-1 text-xs text-gray-500">Provide a brief description of the image/video for accessibility</p>
                  </div>
                </div>
              </div>
              
              {loading && (
                <div className="mt-6">
                  <p className="text-sm font-medium">Uploading... {progress}%</p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${progress}%` }}></div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <button 
                type="button" 
                className="py-2 px-4 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className={`py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white focus:outline-none flex items-center ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                onClick={handleUpload}
                disabled={loading}
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="-ml-1 mr-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                )}
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUploaderTailwind;
